import React, { useState } from "react";

const FileUploadField = ({
  label,
  name,
  accept,
  onChange,
  maxSizeMB = 5,
  errorMessage = "Invalid file type. Allowed: PDF or Word (.pdf, .doc, .docx).",
}) => {
  const [fileInfo, setFileInfo] = useState(null);
  const [error, setError] = useState(null);

  // Parse comma-separated accept list into normalized tokens
  const parseAccept = (acceptStr) =>
    (acceptStr || "")
      .split(",")
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);

  const acceptTokens = parseAccept(accept);

  const isAllowedByAccept = (file) => {
    if (!file) return false;
    const type = (file.type || "").toLowerCase(); // may be empty on some browsers
    const name = (file.name || "").toLowerCase();

    // If no accept provided, allow anything
    if (acceptTokens.length === 0) return true;

    for (const token of acceptTokens) {
      // Extension token: .pdf / .doc / .docx
      if (token.startsWith(".")) {
        if (name.endsWith(token)) return true;
        continue;
      }

      // MIME token: e.g. application/pdf or application/*
      if (token.includes("/")) {
        if (!type) {
          // If MIME is missing, we can’t validate by type; rely on extension tokens
          // Skip and let other tokens (extensions) decide
          continue;
        }
        if (token.endsWith("/*")) {
          // Wildcard main type
          const main = token.split("/")[0];
          if (type.startsWith(main + "/")) return true;
        } else if (type === token) {
          return true;
        }
      }
    }
    return false;
  };

  const handleChange = (e) => {
    const file = e.target.files && e.target.files[0];
    setError(null);

    if (file) {
      // Validate file size
      if (file.size > maxSizeMB * 1024 * 1024) {
        setError(`File size must be less than ${maxSizeMB}MB`);
        e.target.value = "";
        setFileInfo(null);
        if (onChange) onChange(e);
        return; // IMPORTANT: stop here
      }

      // Validate file type by MIME or extension
      if (!isAllowedByAccept(file)) {
        setError(errorMessage);
        e.target.value = "";
        setFileInfo(null);
        if (onChange) onChange(e);
        return; // IMPORTANT: stop here
      }

      setFileInfo({
        name: file.name,
        size: (file.size / (1024 * 1024)).toFixed(2),
      });
    } else {
      setFileInfo(null);
    }

    if (onChange) onChange(e);
  };

  return (
    <div className="flex flex-col space-y-1">
      <label htmlFor={name} className="font-medium text-gray-700">
        {label}
      </label>
      <input
        id={name}
        name={name}
        type="file"
        accept={accept}
        onChange={handleChange}
        className="block"
      />
      {fileInfo ? (
        <div className="text-sm text-gray-600">
          Selected: {fileInfo.name} ({fileInfo.size} MB)
        </div>
      ) : (
        <div className="text-sm text-gray-400">No file selected</div>
      )}
      {error && <div className="text-sm text-red-600">{error}</div>}
    </div>
  );
};

export default FileUploadField;
